import { WOManufacturerMatchStore } from '../wo-manufacturer-match/wo-manufacturer-match-store'
import { WOModelMatchStore } from '../wo-model-match/wo-model-match-store'
import { makeAutoObservable, runInAction } from 'mobx'
import type {
    APIManufacturerAliasReference,
    APIModelAliasReference,
    APIOrganizationProductModelAlias,
} from '@visorpro/client'
import type { APIOrganizationAutoMatchManufacturer } from '@visorpro/client/dist/types/APIOrganizationAutoMatchManufacturer'
import { visorPRORestClient } from '../../../../util/api'
import type { APIOrganizationUnmatchedModel } from '@visorpro/client/dist/types/APIOrganizationUnmatchedModel'
import { observed } from '../../../../util/observed-decorator'

export const MODEL_LIMIT_DEFAULT = 20
export class WOManufacturerModelMatchStore {
    manufacturerAliasStore: WOManufacturerMatchStore
    modelAliasStore: WOModelMatchStore
    manufacturerAliases: APIManufacturerAliasReference[]
    modelAliases: APIModelAliasReference[]
    organizationId: string
    isLoaded: boolean
    loading: boolean
    manufacturerMatches: APIOrganizationAutoMatchManufacturer[]
    unmatchedModels: APIOrganizationUnmatchedModel[]
    autoMatchModelResult?: APIOrganizationProductModelAlias[]
    modelPagination: {
        offset: number
        total: number
        hasMore: boolean
    }
    constructor() {
        makeAutoObservable(this)
        this.organizationId = ''
        this.manufacturerAliasStore = new WOManufacturerMatchStore(
            this.organizationId,
        )
        this.modelAliasStore = new WOModelMatchStore(this.organizationId)
        this.manufacturerAliases = []
        this.modelAliases = []
        this.isLoaded = false
        this.loading = false
        this.manufacturerMatches = []
        this.unmatchedModels = []
        this.modelPagination = {
            offset: 0,
            total: 0,
            hasMore: true,
        }
    }

    @observed('loading')
    public async load(organizationId?: string) {
        if (organizationId) {
            this.organizationId = organizationId
        }
        if (!this.modelPagination.hasMore && this.organizationId) {
            this.loading = false
            return
        }
        try {
            const autoMatchManufacturerResponse =
                await visorPRORestClient.organizationAutoMatchManufacturer.list(
                    this.organizationId,
                )
            const getUnmatchedModelResponse =
                await visorPRORestClient.organizationAutoMatchModel.list({
                    organization_id: this.organizationId,
                    limit: MODEL_LIMIT_DEFAULT,
                    offset: this.modelPagination.offset,
                })
            runInAction(() => {
                this.manufacturerMatches = autoMatchManufacturerResponse
                this.manufacturerMatches.sort(
                    (a, b) =>
                        b.work_order_segments_count -
                        a.work_order_segments_count,
                )
                this.unmatchedModels.push(...getUnmatchedModelResponse.items)
                this.unmatchedModels.sort(
                    (a, b) =>
                        b.work_order_segments_count -
                        a.work_order_segments_count,
                )
                this.modelPagination.offset = getUnmatchedModelResponse.to
                this.modelPagination.total = getUnmatchedModelResponse.total
                this.isLoaded = true
            })
        } catch (e) {
            console.error('Failed to retrieve manufacturer matches:', e)
        }
        this.manufacturerAliasStore = new WOManufacturerMatchStore(
            this.organizationId,
        )
        this.modelAliasStore = new WOModelMatchStore(this.organizationId)
        await this.manufacturerAliasStore.load()
        this.manufacturerAliases = Object.values(
            this.manufacturerAliasStore.aliasesByInputManufacturer,
        ).sort((a, b) =>
            a.input_manufacturer.localeCompare(b.input_manufacturer),
        )
        await this.modelAliasStore.load({
            pageIndex: 0,
            pageSize: 10_000,
        })
        this.modelAliases = Object.values(
            this.modelAliasStore.aliasesByKey,
        ).sort((a, b) => a.input_model.localeCompare(b.input_model))
    }

    @observed('loading')
    public async autoMatchModels(organizationId?: string) {
        try {
            const result =
                await visorPRORestClient.organizationAutoMatchModel.autoMatch({
                    organization_id: organizationId ?? this.organizationId,
                })
            runInAction(() => {
                this.autoMatchModelResult = result
            })
        } catch (e) {
            console.error('Failed to auto match models', e)
        }
        await this.load(this.organizationId)
        return this.autoMatchModelResult
    }

    // TODO:Currently due to alias table structure/store, there is no create only update,change this after table restructure
    createManufacturerAlias = async (
        aliasId: string,
        productManufacturerId: string,
    ) => {
        this.loading = true
        await this.manufacturerAliasStore.update(aliasId, {
            product_manufacturer_id: productManufacturerId,
            is_reviewed: true,
        })
        await this.load(this.organizationId)
        this.loading = false
    }
    createModelAlias = async (aliasId: string, productModelId: string) => {
        this.loading = true
        // await this.modelAliasStore.upsert(aliasId, {
        //   product_model_ids: [productModelId],
        //   is_reviewed: true,
        // })
        await this.load(this.organizationId)
        this.loading = false
    }
}

export const useMatchStore = new WOManufacturerModelMatchStore()
